import { useEffect, useState } from 'react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { OverviewCardTitle } from '@/components/shared/overview/OverviewBreakdownCard'
import { ShareMeter } from '@/components/shared/analytics/AnalyticsReport'
import { getBotTrafficComparison } from '@/lib/api/external-api'
import type { BotTrafficComparisonResponse } from '@/lib/api/external-api'
import { reportBotTrafficFailure } from '@/lib/observability/analytics'
import { useURLState } from '@/hooks/useURLState'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { cn } from '@/lib/utils'

/**
 * Bot traffic as one card on Channels (was the standalone /bots tab, retired
 * 2026-10). Bots are a kind of arrival, so they sit with the sources.
 * Renders `traffic/bots-comparison` verbatim; see SUPENG-190 for why the
 * reported share is likely low (classifier is UA-only today).
 */

type ComparisonData = BotTrafficComparisonResponse['data']
type Reasons = NonNullable<ComparisonData['bot']['reasons']>

const toExternalApiPeriod = (period: string): string => {
  if (period.includes(':')) {
    const [start, end] = period.split(':')
    const diffDays = Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24))
    const granularity = diffDays <= 2 ? 'hour' : diffDays > 90 ? 'week' : 'day'
    return `${granularity}_CUSTOM_${start}_to_${end}`
  }
  const periodMap: Record<string, string> = {
    '1d': 'hour_PAST_24_H',
    '7d': 'day_PAST_7_D',
    '30d': 'day_PAST_30_D',
    '90d': 'day_PAST_3_M',
    '180d': 'week_PAST_6_M',
    '365d': 'week_PAST_12_M',
    '12m': 'month_PAST_12_M',
    'ytd': 'day_PAST_12_M',
    'mtd': 'day_PAST_30_D',
  }
  return periodMap[period] || 'day_PAST_7_D'
}

const REASON_LABELS: { key: keyof Reasons; label: string }[] = [
  { key: 'verified_crawler', label: 'Verified crawlers' },
  { key: 'ua_pattern', label: 'Bot user agents' },
  { key: 'datacenter_asn', label: 'Datacenter traffic' },
  { key: 'behavioral', label: 'Behavioral signals' },
]

export function BotTrafficCard({
  propertyId,
  token,
  className,
}: {
  propertyId?: string
  token?: string | null
  className?: string
}) {
  const { state } = useURLState()
  // The FilterBar writes filters to URL state, so refetch off the URL filters.
  const filtersKey = JSON.stringify(state.filters)
  const [data, setData] = useState<ComparisonData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!propertyId || !token) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    const run = async () => {
      setIsLoading(true)
      try {
        const filterParams: Record<string, string> = {}
        for (const f of state.filters) {
          if (f.operator === ':') filterParams[f.field] = f.value
          else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value
        }
        const response = await getBotTrafficComparison(
          toExternalApiPeriod(state.period || '7d'),
          propertyId,
          token,
          filterParams,
        )
        if (!cancelled) setData(response.data)
      } catch (error) {
        reportBotTrafficFailure(error, { propertyId }, { period: state.period })
        if (!cancelled) setData(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // filtersKey stands in for state.filters (new array identity each render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, token, state.period, filtersKey])

  const reasons = data?.bot.reasons ?? null
  const reasonTotal = reasons ? REASON_LABELS.reduce((sum, r) => sum + (reasons[r.key] ?? 0), 0) : 0

  return (
    <Card className={cn('gap-0 py-0', className)}>
      <div className="flex min-h-[58px] items-center gap-2.5 px-[18px] py-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <OverviewCardTitle>Bot traffic</OverviewCardTitle>
          <p className="text-xs leading-4 text-muted-foreground">Automated visits detected in this range</p>
        </div>
        {isLoading ? (
          <Skeleton className="h-4 w-24" />
        ) : data ? (
          <span className="text-[13px] leading-4 text-muted-foreground">
            <span className="font-medium text-foreground tabular-nums">{data.bot.pct}%</span> of page views
          </span>
        ) : null}
      </div>

      {isLoading ? (
        <div className="border-t border-border/60 px-[18px] py-4">
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ) : !data ? (
        <div className="border-t border-border/60 px-[18px] py-8 text-center text-[13px] text-muted-foreground">
          No bot traffic data for this range.
        </div>
      ) : reasons && reasonTotal > 0 ? (
        <div className="flex flex-col border-t border-border/60">
          {REASON_LABELS.map((r) => {
            const value = reasons[r.key] ?? 0
            return (
              <div
                key={r.key}
                className="flex h-11 shrink-0 items-center gap-3 border-b border-border/60 px-[18px] last:border-0"
              >
                <span className="min-w-0 flex-1 truncate text-sm font-medium leading-[18px] text-foreground">{r.label}</span>
                <span className="w-[110px] shrink-0 text-right text-[13px] font-medium leading-4 text-foreground tabular-nums">
                  {formatNumeric(value)}
                </span>
                <span className="hidden w-[110px] shrink-0 sm:block">
                  <ShareMeter percent={(value / reasonTotal) * 100} />
                </span>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="border-t border-border/60 px-[18px] py-3.5 text-[13px] leading-[18px] text-foreground tabular-nums">
          {formatNumeric(data.bot.page_views)} bot page views
          <span className="text-muted-foreground"> · {formatNumeric(data.bot.unique_visitors)} bot visitors</span>
        </div>
      )}

      {!isLoading && data && (
        <p className="border-t border-border/60 px-[18px] py-3 text-xs leading-4 text-muted-foreground">
          Detection reads browser signatures today, so some bots still pass as visitors.
        </p>
      )}
    </Card>
  )
}
